import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getApiEndpoint } from '../config/apiConfig';
import { createRetryFn } from '../config/react-query';
import { isDevelopment } from '../utils/environment';

// Use proxy in development, direct URL in production
const getTokenApiUri = (): string => {
  const isDevMode = isDevelopment();
  return getApiEndpoint('/reservation/app/token', isDevMode);
};

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  label = 'request',
): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timeout after ${timeoutMs}ms`)),
      timeoutMs,
    );
    promise
      .then((val) => {
        clearTimeout(timer);
        resolve(val);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

/**
 * The meeting JWT is either an SSO (idToken) reservation or a named guest
 * reservation. Do not fetch until auth has settled — a request with neither
 * identity is treated as anonymous and Jitsi then prompts for a display name
 * that disappears once SSO catches up.
 */
export const shouldFetchMeetingToken = ({
  authSettled,
  jitsiMeetId,
  room,
  idToken,
  guestName,
}: {
  authSettled: boolean;
  jitsiMeetId: string;
  room: string | null;
  idToken: string | null;
  guestName?: string | null;
}): boolean => authSettled && !!jitsiMeetId && !!room && (!!idToken || !!guestName);

export const useFetchToken = (
  idToken: string | null,
  jitsiMeetId: string,
  room: string | null,
  guestName?: string | null,
  authSettled: boolean = true,
) => {
  return useQuery({
    enabled: shouldFetchMeetingToken({
      authSettled,
      jitsiMeetId,
      room,
      idToken,
      guestName,
    }),
    retry: createRetryFn({ maxRetriesOn401: 0 }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    // Keep previous token data while refetching to prevent meeting from hiding during token refresh
    placeholderData: keepPreviousData,
    queryKey: ['token', idToken, jitsiMeetId, room, guestName],
    queryFn: async () => {
      // Build headers conditionally - only add Authorization if idToken is present
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }

      const res = await withTimeout(
        fetch(getTokenApiUri(), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            room,
            jitsiMeetId,
            ...(guestName && !idToken ? { guestName } : {}),
          }),
        }),
        15000,
        'Fetch meeting token',
      );

      if (!res.ok) {
        const text = await res.text();
        const error = new Error(`error ${res.status}: ${text}`) as Error & { status: number };
        error.status = res.status;
        throw error;
      }

      const data = await res.text();

      if (!data) {
        throw new Error('Error: no token returned');
      }

      return data;
    },
  });
};
